import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { DeleteIcon } from "@chakra-ui/icons";
import {
	Button,
	Modal,
	ModalOverlay,
	ModalHeader,
	ModalContent,
	ModalCloseButton,
	ModalBody,
	ModalFooter,
	Td,
	Text,
	Tr,
	Tooltip,
	Icon,
	useDisclosure,
	Stack,
	HStack,
	VStack,
	Card,
	CardBody,
	CardHeader,
	Heading,
	SimpleGrid,
	Badge,
	useColorModeValue,
} from "@chakra-ui/react";
import moment from "moment";
import { FiActivity, FiCalendar, FiFileText, FiXCircle, FiInfo, FiCheckCircle } from "react-icons/fi";

import { removeActivityAsync } from "../../redux/user/userSlice";
import { fullDateTimeFormat } from "../../utils/types";

export default function ActivitiesTableRow({ activity, property }) {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	// Color mode values
	const cardBg = useColorModeValue("white", "gray.800");
	const borderColor = useColorModeValue("gray.200", "gray.700");
	const iconColor = useColorModeValue("blue.500", "blue.300");

	const deleteActivity = (activityId) => {
		const payload = { activityId, propertyId: property.id };
		console.log("delete: ", payload);
		dispatch(removeActivityAsync(payload));
	};
	const saveActivity = () => {};
	const renderActivityModal = () => (
		<Modal isOpen={isOpen} onClose={onClose} size="2xl">
			<ModalOverlay backdropFilter="blur(10px)" />
			<ModalContent borderRadius="xl" boxShadow="2xl">
				<ModalHeader borderBottom="1px" borderColor={borderColor} pb={4}>
					<HStack spacing={3}>
						<Icon as={FiActivity} color={iconColor} boxSize={6} />
						<VStack align="start" spacing={0}>
							<Heading size="md">Activity Details</Heading>
							<Text fontSize="sm" color="gray.500">
								View activity information
							</Text>
						</VStack>
					</HStack>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody py={6}>
					<SimpleGrid columns={1} spacing={2}>
						{/* Activity Information Card */}
						<Card bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg">
							<CardHeader pb={3}>
								<HStack spacing={3}>
									<Icon as={FiInfo} color={iconColor} boxSize={5} />
									<Text fontWeight="semibold" fontSize="md">
										Activity Information
									</Text>
								</HStack>
							</CardHeader>
							<CardBody pt={0}>
								<VStack spacing={4} align="stretch">
									<HStack alignItems={"center"}>
										<HStack spacing={2} alignItems={"center"}>
											<Icon as={FiFileText} color={iconColor} boxSize={4} />
											<Text fontSize="sm" fontWeight="medium">
												Title:
											</Text>
										</HStack>
										<Text fontSize="lg" fontWeight="bold">
											{activity.title}
										</Text>
									</HStack>

									<HStack alignItems={"center"}>
										<HStack spacing={2} alignItems={"center"}>
											<Icon as={FiActivity} color={iconColor} boxSize={4} />
											<Text fontSize="sm" fontWeight="medium">
												Type:
											</Text>
										</HStack>
										<Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
											{activity.type}
										</Badge>
									</HStack>

									<HStack alignItems={"center"}>
										<HStack spacing={2} alignItems={"center"}>
											<Icon as={FiCalendar} color={iconColor} boxSize={4} />
											<Text fontSize="sm" fontWeight="medium">
												Date Added:
											</Text>
										</HStack>
										<Text fontSize="sm" fontWeight="medium">
											{moment(activity.addedDate).format(fullDateTimeFormat)}
										</Text>
									</HStack>
								</VStack>
							</CardBody>
						</Card>

						{/* Description Card */}
						<Card bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg">
							<CardHeader pb={3}>
								<HStack spacing={3}>
									<Icon as={FiFileText} color={iconColor} boxSize={5} />
									<Text fontWeight="semibold" fontSize="md">
										Description
									</Text>
								</HStack>
							</CardHeader>
							<CardBody pt={0}>
								<Text fontSize="sm" color="gray.700" bg="gray.50" p={4} borderRadius="md" minH="120px">
									{activity.description}
								</Text>
							</CardBody>
						</Card>
					</SimpleGrid>
				</ModalBody>

				<ModalFooter borderTop="1px" borderColor={borderColor} pt={4}>
					<HStack spacing={3}>
						<Button leftIcon={<Icon as={FiXCircle} />} variant="outline" onClick={onClose}>
							{t("misc.cancel")}
						</Button>
						<Button colorScheme="blue" leftIcon={<Icon as={FiCheckCircle} />} onClick={saveActivity} px={8}>
							{t("misc.save")}
						</Button>
					</HStack>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
	return (
		<Tr>
			<Td>
				<Text>{moment(activity.addedDate).format(fullDateTimeFormat)}</Text>
			</Td>
			<Td>
				<Text>{activity.title}</Text>
			</Td>
			<Td>
				<Text>{activity.type}</Text>
			</Td>
			<Td>
				<Text>{activity.description}</Text>
			</Td>
			<Td>
				<Stack direction="row">
					<Button onClick={onOpen}>View</Button>
					{renderActivityModal()}
					<Button
						colorScheme={"red"}
						onClick={() => {
							deleteActivity(activity.id);
						}}
					>
						<Tooltip label="Delete">
							<Icon as={DeleteIcon} boxSize={6}></Icon>
						</Tooltip>
					</Button>
				</Stack>
			</Td>
		</Tr>
	);
}
